import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Badge,
  LinearProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import HotelIcon from '@mui/icons-material/Hotel';
import LayersIcon from '@mui/icons-material/Layers';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ImageIcon from '@mui/icons-material/Image';
import SpaIcon from '@mui/icons-material/Spa';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import NotchTabs from '../components/common/NotchTabs';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import {
  getDashboardPageShellSx,
  getDashboardSectionHeaderDividerSx,
  getDashboardSectionNavButtonSx,
  getDashboardSectionTabCardSx,
} from '../utils/dashboardSurfaceSx';
import {
  getConfigurationContentPaperSx,
  getConfigurationProgressCardSx,
  getConfigurationProgressLabelSx,
  getConfigurationProgressValueSx,
} from '../utils/configurationMobileLayout';
import BasicDetailsPage from './BasicDetailsPage';
import FinanceSetupPage from './FinanceSetupPage';
import InvoiceSetupPage from './InvoiceSetupPage';
import DataMigrationPage from './DataMigrationPage';
import RatePlanPage from './RatePlanPage';
import RoomTypePage from './RoomTypePage';
import FloorPage from './FloorPage';
import RoomPage from './RoomPage';
import FileManagerPage from './FileManagerPage';
import ConfigPage from './ConfigPage';
import TermsAndPolicyPage from './TermsAndPolicyPage';
import AmenitiesPage from './AmenitiesPage';
import HotelConfiguration from '../components/configuration/HotelConfiguration';
import axios from 'axios';

// Get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const HOTEL_CONFIG_FIELDS = ['timezone', 'country', 'currency'];

function buildConfigSectionProgress(hotelConfig = {}) {
  const filledCount = HOTEL_CONFIG_FIELDS.filter(
    (field) => hotelConfig[field] && String(hotelConfig[field]).trim() !== ''
  ).length;
  return {
    progress: Math.round((filledCount / HOTEL_CONFIG_FIELDS.length) * 100),
    status:
      filledCount === HOTEL_CONFIG_FIELDS.length
        ? 'completed'
        : filledCount > 0
          ? 'active'
          : 'pending',
    count: filledCount,
  };
}

// Theme color constant
const THEME_COLOR = '#4ce1b6';

/** Stable reference to avoid re-running scroll effects each render. */
const CONFIG_SECTIONS = [
  {
    id: 'config',
    title: 'Config',
    shortLabel: 'Config',
    icon: SettingsIcon,
    status: 'active',
    progress: 0,
    count: 0,
    url: '/configuration/config/',
  },
  {
    id: 'basic-details',
    title: 'Basic Details',
    shortLabel: 'Basic',
    icon: BusinessIcon,
    status: 'active', // 'active', 'completed', 'pending'
    progress: 60,
    count: null,
    url: '/configuration/basic-details/',
  },
  {
    id: 'rate-plan',
    title: 'Rate Plan',
    shortLabel: 'Rates',
    icon: DescriptionIcon,
    status: 'completed',
    progress: 100,
    count: 1,
    url: '/configuration/rate-plan/',
  },
  {
    id: 'room-types',
    title: 'Room types',
    shortLabel: 'Types',
    icon: HotelIcon,
    status: 'completed',
    progress: 100,
    count: 6,
    url: '/configuration/room-types/',
  },
  {
    id: 'floors',
    title: 'Floors',
    shortLabel: 'Floors',
    icon: LayersIcon,
    status: 'completed',
    progress: 100,
    count: 10,
    url: '/configuration/floors/',
  },
  {
    id: 'rooms',
    title: 'Rooms',
    shortLabel: 'Rooms',
    icon: MeetingRoomIcon,
    status: 'completed',
    progress: 100,
    count: 16,
    url: '/configuration/rooms/',
  },
  {
    id: 'images',
    title: 'Images',
    shortLabel: 'Images',
    icon: ImageIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/configuration/images/',
  },
  {
    id: 'amenities',
    title: 'Amenities',
    shortLabel: 'Amenity',
    icon: SpaIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/configuration/amenities/',
  },
  {
    id: 'terms-and-policy',
    title: 'Terms & Policy',
    shortLabel: 'Terms',
    icon: GavelIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/configuration/terms-and-policy/',
  },
  {
    id: 'finance-setup',
    title: 'Finance Setup',
    shortLabel: 'Finance',
    icon: AccountBalanceIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/configuration/finance-setup/',
  },
  {
    id: 'invoice-setup',
    title: 'Invoice Setup',
    shortLabel: 'Invoice',
    icon: ReceiptLongIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/configuration/invoice-setup/',
  },
  {
    id: 'data-migration',
    title: 'Data Migration',
    shortLabel: 'Migrate',
    icon: CloudUploadIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/configuration/data-migration/',
  },
];

const ConfigurationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  
  // Determine active section from URL
  const getActiveSectionFromPath = () => {
    if (location.pathname.includes('/basic-details')) return 'basic-details';
    if (location.pathname.includes('/rate-plan')) return 'rate-plan';
    if (location.pathname.includes('/room-types')) return 'room-types';
    if (location.pathname.includes('/floors')) return 'floors';
    if (location.pathname.includes('/rooms')) return 'rooms';
    if (location.pathname.includes('/images')) return 'images';
    if (location.pathname.includes('/amenities')) return 'amenities';
    if (location.pathname.includes('/terms-and-policy')) return 'terms-and-policy';
    if (location.pathname.includes('/finance-setup')) return 'finance-setup';
    if (location.pathname.includes('/invoice-setup')) return 'invoice-setup';
    if (location.pathname.includes('/data-migration')) return 'data-migration';
    if (location.pathname.includes('/config')) return 'config';
    if (location.pathname.includes('/communications')) return 'config';
    if (location.pathname === '/configuration/' || location.pathname.endsWith('/configuration')) {
      return 'config';
    }
    return 'config';
  };
  
  const [activeSection, setActiveSection] = useState(getActiveSectionFromPath());
  const cardsContainerRef = useRef(null);
  const [sectionsData, setSectionsData] = useState({});
  const [loading, setLoading] = useState(true);
  // Carousel arrows: scroll strip when possible; otherwise move section.
  const [carouselScroll, setCarouselScroll] = useState({ canLeft: false, canRight: false });

  // Update active section when route changes
  useEffect(() => {
    setActiveSection(getActiveSectionFromPath());
  }, [location.pathname]);

  // Scroll to active card when section changes
  useEffect(() => {
    if (cardsContainerRef.current) {
      const activeIndex = CONFIG_SECTIONS.findIndex(s => s.id === activeSection);
      if (activeIndex !== -1) {
        const cardWidth = 180 + 16; // card width + gap
        const scrollPosition = activeIndex * cardWidth;
        cardsContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }
    }
  }, [activeSection]);

  const updateCarouselScrollState = useCallback(() => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const idx = CONFIG_SECTIONS.findIndex((s) => s.id === activeSection);
    const len = CONFIG_SECTIONS.length;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const eps = 2;
    const atScrollEnd = scrollLeft + clientWidth >= scrollWidth - eps;
    setCarouselScroll({
      canLeft: !(scrollLeft <= eps && idx <= 0),
      canRight: !(atScrollEnd && idx >= len - 1),
    });
  }, [activeSection]);

  useEffect(() => {
    const t = window.setTimeout(() => updateCarouselScrollState(), 450);
    return () => clearTimeout(t);
  }, [activeSection, updateCarouselScrollState]);

  useEffect(() => {
    const el = cardsContainerRef.current;
    if (!el) return undefined;
    updateCarouselScrollState();
    const onScroll = () => updateCarouselScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateCarouselScrollState());
    ro.observe(el);
    window.addEventListener('resize', updateCarouselScrollState);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateCarouselScrollState);
    };
  }, [updateCarouselScrollState, loading, sectionsData]);

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // Fetch section data and calculate progress
  useEffect(() => {
    const fetchSectionData = async () => {
      setLoading(true);
      try {
        const csrftoken = getCookie('csrftoken');
        const headers = {
          'Accept': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        };

        const data = {};

        // Fetch Basic Details
        try {
          const basicDetailsRes = await axios.get('/configuration/api/basic-details/', { withCredentials: true, headers });
          if (basicDetailsRes.data && basicDetailsRes.data.hotel) {
            const hotel = basicDetailsRes.data.hotel;
            const fields = ['name', 'description', 'address', 'city', 'state', 'country', 'postal_code', 'phone', 'email', 'website'];
            const filledFields = fields.filter(field => hotel[field] && hotel[field].toString().trim() !== '').length;
            data['basic-details'] = {
              progress: Math.round((filledFields / fields.length) * 100),
              status: filledFields === fields.length ? 'completed' : filledFields > 0 ? 'active' : 'pending',
            };
          }
        } catch (e) {
          data['basic-details'] = { progress: 0, status: 'pending' };
        }

        // Fetch Rate Plans
        try {
          const ratePlansRes = await axios.get('/configuration/api/rate-plans/', { withCredentials: true, headers });
          const count = ratePlansRes.data?.rate_plans?.length || 0;
          data['rate-plan'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['rate-plan'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Room Types
        try {
          const roomTypesRes = await axios.get('/configuration/api/room-types/', { withCredentials: true, headers });
          const count = roomTypesRes.data?.room_types?.length || 0;
          data['room-types'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['room-types'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Floors
        try {
          const floorsRes = await axios.get('/configuration/api/floors/', { withCredentials: true, headers });
          const count = floorsRes.data?.floors?.length || 0;
          data['floors'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['floors'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Rooms
        try {
          const roomsRes = await axios.get('/configuration/api/rooms/', { withCredentials: true, headers });
          // Calculate total rooms from all floors
          let totalRooms = 0;
          if (roomsRes.data?.rooms_by_floor && Array.isArray(roomsRes.data.rooms_by_floor)) {
            roomsRes.data.rooms_by_floor.forEach((floorGroup) => {
              if (floorGroup.rooms && Array.isArray(floorGroup.rooms)) {
                totalRooms += floorGroup.rooms.length;
              }
            });
          }
          // Fallback to direct rooms array if rooms_by_floor is not available
          if (totalRooms === 0 && roomsRes.data?.rooms && Array.isArray(roomsRes.data.rooms)) {
            totalRooms = roomsRes.data.rooms.length;
          }
          // Progress: 100% if more than 1 room, 50% if 1 room, 0% if no rooms
          // Status: completed if more than 1 room, active if 1 room, pending if no rooms
          data['rooms'] = {
            progress: totalRooms > 1 ? 100 : (totalRooms === 1 ? 50 : 0),
            status: totalRooms > 1 ? 'completed' : (totalRooms === 1 ? 'active' : 'pending'),
            count: totalRooms,
          };
        } catch (e) {
          data['rooms'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Images
        try {
          const imagesRes = await axios.get('/configuration/api/files/', { withCredentials: true, headers });
          const count = imagesRes.data?.files?.length || 0;
          data['images'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['images'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Policies
        try {
          const policiesRes = await axios.get('/configuration/api/policies/', { withCredentials: true, headers });
          const policies = policiesRes.data?.policies || [];
          const policyTypes = ['terms_and_conditions', 'privacy_policy', 'child_policy', 'cancellation_policy'];
          const filledTypes = policyTypes.filter(type => policies.some(p => p.policy_type === type)).length;
          data['terms-and-policy'] = {
            progress: Math.round((filledTypes / policyTypes.length) * 100),
            status: filledTypes === policyTypes.length ? 'completed' : filledTypes > 0 ? 'active' : 'pending',
            count: policies.length,
          };
        } catch (e) {
          data['terms-and-policy'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Hotel config — timezone, country, currency (3 fields → 100% when all set)
        try {
          const hotelConfigRes = await axios.get('/onboarding/api/hotel/configuration/', {
            withCredentials: true,
            headers,
          });
          data['config'] = buildConfigSectionProgress(hotelConfigRes.data);
        } catch (e) {
          data['config'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Finance Setup - check for tax rules
        try {
          const taxRulesRes = await axios.get('/configuration/api/tax-rules/', { withCredentials: true, headers });
          const taxRules = taxRulesRes.data?.tax_rules || [];
          data['finance-setup'] = {
            progress: taxRules.length > 0 ? 100 : 0,
            status: taxRules.length > 0 ? 'completed' : 'pending',
            count: taxRules.length,
          };
        } catch (e) {
          data['finance-setup'] = { progress: 0, status: 'pending', count: 0 };
        }

        try {
          const invoiceSetupRes = await axios.get('/configuration/api/invoice-setup/', {
            withCredentials: true,
            headers,
          });
          const activeTemplateId = invoiceSetupRes.data?.settings?.active_template_id;
          const templateCount = invoiceSetupRes.data?.templates?.length || 0;
          data['invoice-setup'] = {
            progress: activeTemplateId ? 100 : 0,
            status: activeTemplateId ? 'completed' : 'pending',
            count: templateCount,
          };
        } catch (e) {
          data['invoice-setup'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Amenities
        try {
          const amenitiesRes = await axios.get('/configuration/api/amenities/', { withCredentials: true, headers });
          // Count total amenities across all categories
          let totalAmenities = 0;
          if (amenitiesRes.data?.categories && Array.isArray(amenitiesRes.data.categories)) {
            amenitiesRes.data.categories.forEach((category) => {
              if (category.amenities && Array.isArray(category.amenities)) {
                totalAmenities += category.amenities.length;
              }
            });
          }
          // Progress: 100% if more than 1 amenity, 50% if 1 amenity, 0% if no amenities
          // Status: completed if more than 1 amenity, active if 1 amenity, pending if no amenities
          data['amenities'] = {
            progress: totalAmenities > 1 ? 100 : (totalAmenities === 1 ? 50 : 0),
            status: totalAmenities > 1 ? 'completed' : (totalAmenities === 1 ? 'active' : 'pending'),
            count: totalAmenities,
          };
        } catch (e) {
          data['amenities'] = { progress: 0, status: 'pending', count: 0 };
        }

        setSectionsData(data);
      } catch (error) {} finally {
        setLoading(false);
      }
    };

    fetchSectionData();
  }, []);

  // Refresh Config card progress when timezone/country/currency is saved
  useEffect(() => {
    const refreshConfigProgress = async () => {
      try {
        const csrftoken = getCookie('csrftoken');
        const headers = {
          Accept: 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        };
        const res = await axios.get('/onboarding/api/hotel/configuration/', {
          withCredentials: true,
          headers,
        });
        setSectionsData((prev) => ({ ...prev, config: buildConfigSectionProgress(res.data) }));
      } catch {
        setSectionsData((prev) => ({
          ...prev,
          config: { progress: 0, status: 'pending', count: 0 },
        }));
      }
    };

    window.addEventListener('hotel-config-updated', refreshConfigProgress);
    return () => window.removeEventListener('hotel-config-updated', refreshConfigProgress);
  }, []);

  const isDark = currentTheme === 'dark';
  const isMobileLayout = isMobileShellLayout();

  const activeTabIndex = useMemo(
    () => Math.max(0, CONFIG_SECTIONS.findIndex((s) => s.id === activeSection)),
    [activeSection],
  );

  const configurationNotchTabs = useMemo(
    () => CONFIG_SECTIONS.map(({ icon, title, shortLabel }) => ({
      icon,
      label: title,
      shortLabel,
    })),
    [],
  );

  const setupProgress = useMemo(() => {
    const completed = CONFIG_SECTIONS.filter((section) => {
      const data = sectionsData[section.id] || {};
      return (data.status || section.status) === 'completed';
    }).length;
    const total = CONFIG_SECTIONS.length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percent };
  }, [sectionsData]);

  const handleSectionClick = (section) => {
    setActiveSection(section.id);
    if (section.url) {
      navigate(section.url);
    }
  };

  // Navigation functions
  const getCurrentSectionIndex = () => {
    return CONFIG_SECTIONS.findIndex(s => s.id === activeSection);
  };

  const handlePrevious = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      const previousSection = CONFIG_SECTIONS[currentIndex - 1];
      handleSectionClick(previousSection);
    }
  };

  const handleNext = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < CONFIG_SECTIONS.length - 1) {
      const nextSection = CONFIG_SECTIONS[currentIndex + 1];
      handleSectionClick(nextSection);
    }
  };

  const CARD_STRIP_STEP = 180 + 16;

  // Prefer strip scrolling; at strip edges, move section.
  const handleCarouselArrowLeft = () => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const eps = 2;
    const { scrollLeft } = el;
    if (scrollLeft > eps) {
      el.scrollBy({ left: -CARD_STRIP_STEP, behavior: 'smooth' });
    } else {
      const idx = getCurrentSectionIndex();
      if (idx > 0) handleSectionClick(CONFIG_SECTIONS[idx - 1]);
    }
  };

  const handleCarouselArrowRight = () => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const eps = 2;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - eps;
    if (!atEnd) {
      el.scrollBy({ left: CARD_STRIP_STEP, behavior: 'smooth' });
    } else {
      const idx = getCurrentSectionIndex();
      if (idx >= 0 && idx < CONFIG_SECTIONS.length - 1) {
        handleSectionClick(CONFIG_SECTIONS[idx + 1]);
      }
    }
  };

  const currentIndex = getCurrentSectionIndex();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < CONFIG_SECTIONS.length - 1;
  const { canLeft: canScrollCarouselLeft, canRight: canScrollCarouselRight } = carouselScroll;

  // Use theme color for all cards and progress bars
  const cardColor = THEME_COLOR;
  const progressColor = THEME_COLOR;

  return (
    <Box sx={getDashboardPageShellSx(currentTheme)}>
      <Box sx={{ mb: isMobileLayout ? 1 : 3 }}>
          <DashboardHeader
            title="Configuration"
            forceDark={isMobileLayout}
            navLinks={isMobileLayout ? [] : [
              { label: 'Dynamic Pricing', url: '/dynamic-pricing/' },
              { label: 'Distribution', url: '/distribution/' }
            ]}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Configuration' }
            ]}
          />
      </Box>

      {isMobileLayout && (
        <Box sx={getConfigurationProgressCardSx()}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography sx={getConfigurationProgressLabelSx()}>Hotel setup</Typography>
            <Typography sx={getConfigurationProgressValueSx(THEME_COLOR)}>
              {setupProgress.completed}/{setupProgress.total} complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={setupProgress.percent}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: THEME_COLOR,
              },
            }}
          />
        </Box>
      )}

      {isMobileLayout ? (
        <Box sx={{ mb: 1.5 }}>
          <NotchTabs
            value={activeTabIndex}
            onChange={(_, index) => handleSectionClick(CONFIG_SECTIONS[index])}
            tabs={configurationNotchTabs}
            accentColor={THEME_COLOR}
            mobileRows={2}
            sx={{ width: '100%', m: 0 }}
          />
        </Box>
      ) : (
      <Box
        sx={{
          position: 'relative',
          mb: 3,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Left Arrow - Full Height */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 48,
            backgroundColor: 'transparent !important',
            zIndex: 10,
            cursor: canScrollCarouselLeft ? 'pointer' : 'not-allowed',
            opacity: canScrollCarouselLeft ? 1 : 0.3,
          }}
          onClick={canScrollCarouselLeft ? handleCarouselArrowLeft : undefined}
        >
          <IconButton
            disabled={!canScrollCarouselLeft}
            sx={{
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
              backgroundColor: 'transparent',
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {/* Cards Container */}
        <Box
          ref={cardsContainerRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            pt: 2,
            pb: 2,
            flex: 1,
            minWidth: 0,
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Opera
            },
            scrollBehavior: 'smooth',
          }}
        >
        {CONFIG_SECTIONS.map((section) => {
          const IconComponent = section.icon;
          const isActive = activeSection === section.id;
          const sectionData = sectionsData[section.id] || {};
          const sectionProgress = sectionData.progress !== undefined ? sectionData.progress : section.progress;
          const sectionStatus = sectionData.status || section.status;
          const sectionCount = sectionData.count !== undefined ? sectionData.count : section.count;

          return (
            <Paper
              key={section.id}
              elevation={0}
              onClick={() => handleSectionClick(section)}
              sx={getDashboardSectionTabCardSx(isDark, isActive, cardColor)}
            >
              {/* Badge for count */}
              {sectionCount !== null && sectionCount !== undefined && (
                <Badge
                  badgeContent={sectionCount}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    '& .MuiBadge-badge': {
                      backgroundColor: THEME_COLOR,
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 600,
                      minWidth: '24px',
                      height: '24px',
                      borderRadius: '12px',
                    },
                  }}
                />
              )}

              {/* Icon */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '50%',
                    backgroundColor: isActive
                      ? isDark
                        ? 'rgba(76, 225, 182, 0.2)'
                        : 'rgba(76, 225, 182, 0.1)'
                      : isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 40,
                      color: isActive ? THEME_COLOR : (isDark ? '#e2e8f0' : '#666'),
                      transition: 'all 0.3s ease',
                    }}
                  />
                </Box>
              </Box>

              {/* Title */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: isActive ? 700 : 600,
                  color: isActive
                    ? THEME_COLOR
                    : isDark
                    ? '#e2e8f0'
                    : '#212121',
                  textAlign: 'center',
                  mb: 2,
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                }}
              >
                {section.title}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ mt: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isActive
                        ? isDark
                          ? '#f8fafc'
                          : '#4ce1b6'
                        : isDark
                        ? 'rgba(148, 163, 184, 0.92)'
                        : '#757575',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '0.75rem',
                    }}
                  >
                    {sectionStatus === 'completed' ? 'Completed' : sectionStatus === 'active' ? 'In Progress' : 'Pending'}
                  </Typography>
                  {sectionCount !== null && sectionCount !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isActive
                          ? THEME_COLOR
                          : isDark
                          ? 'rgba(148, 163, 184, 0.92)'
                          : '#757575',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    >
                      {sectionCount} items
                    </Typography>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={sectionProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isActive
                      ? isDark
                        ? 'rgba(76, 225, 182, 0.1)'
                        : 'rgba(76, 225, 182, 0.1)'
                      : isDark
                      ? 'rgba(255,255,255,0.08)'
                      : '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: sectionProgress > 0 ? THEME_COLOR : (isDark ? 'rgba(255,255,255,0.12)' : '#bdbdbd'),
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                    },
                  }}
                />
              </Box>
            </Paper>
          );
        })}
        </Box>

        {/* Right Arrow - Full Height */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 48,
            backgroundColor: 'transparent !important',
            zIndex: 10,
            cursor: canScrollCarouselRight ? 'pointer' : 'not-allowed',
            opacity: canScrollCarouselRight ? 1 : 0.3,
          }}
          onClick={canScrollCarouselRight ? handleCarouselArrowRight : undefined}
        >
          <IconButton
            disabled={!canScrollCarouselRight}
            sx={{
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
              backgroundColor: 'transparent',
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      )}

      {/* Content Area - Show content based on active section */}
      <Paper
        elevation={0}
        sx={{
          ...getConfigurationContentPaperSx(isDark, isMobileLayout),
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {!isMobileLayout && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: getDashboardSectionHeaderDividerSx(isDark),
          }}
        >
          <IconButton
            onClick={handlePrevious}
            disabled={!hasPrevious}
            sx={getDashboardSectionNavButtonSx(isDark, hasPrevious, THEME_COLOR)}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isDark ? '#f8fafc' : '#212121',
              flexGrow: 1,
              textAlign: 'center',
            }}
          >
            {CONFIG_SECTIONS.find(s => s.id === activeSection)?.title || 'Configuration'}
          </Typography>

          <IconButton
            onClick={handleNext}
            disabled={!hasNext}
            sx={getDashboardSectionNavButtonSx(isDark, hasNext, THEME_COLOR)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        )}

        {/* Section Content */}
        {activeSection === 'basic-details' ? (
          <BasicDetailsPage />
        ) : activeSection === 'rate-plan' ? (
          <RatePlanPage />
        ) : activeSection === 'room-types' ? (
          <RoomTypePage />
        ) : activeSection === 'floors' ? (
          <FloorPage />
        ) : activeSection === 'rooms' ? (
          <RoomPage />
        ) : activeSection === 'finance-setup' ? (
          <FinanceSetupPage showHeader={false} />
        ) : activeSection === 'invoice-setup' ? (
          <InvoiceSetupPage showHeader={false} />
        ) : activeSection === 'data-migration' ? (
          <DataMigrationPage showHeader={false} />
        ) : activeSection === 'config' ? (
          <ConfigPage showHeader={false} />
        ) : activeSection === 'images' ? (
          <FileManagerPage />
        ) : activeSection === 'amenities' ? (
          <AmenitiesPage showHeader={false} />
        ) : activeSection === 'terms-and-policy' ? (
          <TermsAndPolicyPage showHeader={false} />
        ) : (
          <Box>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? '#999' : '#757575',
                textAlign: 'center',
                py: 8,
              }}
            >
              {`${CONFIG_SECTIONS.find(s => s.id === activeSection)?.title} configuration content will be displayed here.`}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ConfigurationPage;
